import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useStytchUser } from '@stytch/react';
import { anonymousDashboardUserId, isAnonymousDashboardEnabled, isStytchEnabled, isSaaSMode } from '../utils/runtimeConfig';
import { setProxyIdToken, isUnauthenticatedError, handleAuthenticationFailure } from '../utils/auth';

const AppAuthContext = createContext({
  user: null,
  isAuthenticated: false,
  isInitialized: false,
  isHermetiqAdmin: false,
  hasMcpConnected: false,
  authSource: null,
  groups: [],
  refreshUser: async () => null,
});

const PROXY_AUTH_IDENTITY_TIMEOUT_MS = 10000;

function normalizeMetadataBoolean(value) {
  return value === true || value === 'true';
}

function normalizeStytchUser(user) {
  if (!user) return null;

  return {
    ...user,
    id: user.id || user.user_id || user.emails?.[0]?.email || '',
    email: user.email || user.emails?.[0]?.email || '',
    auth_source: 'stytch',
    groups: Array.isArray(user.groups) ? user.groups : [],
    trusted_metadata: user.trusted_metadata || {},
  };
}

function parseGroups(rawGroups) {
  if (!rawGroups) return [];

  return rawGroups
    .split(',')
    .map(group => group.trim())
    .filter(Boolean);
}

function extractIdToken(headers) {
  const raw = headers.get('X-Auth-Id-Token')?.trim() || '';
  if (raw.startsWith('Bearer ')) {
    return raw.slice(7);
  }
  return raw || null;
}

function normalizeProxyUser(headers) {
  const username = headers.get('X-Forwarded-UserUsername')?.trim() || '';
  const email = headers.get('X-Forwarded-Email')?.trim() || '';
  const preferredUsername = headers.get('X-Forwarded-Preferred-Username')?.trim() || '';
  const groups = parseGroups(headers.get('X-Forwarded-Groups'));
  const idToken = extractIdToken(headers);
  const userId = preferredUsername || username || email;

  if (!userId && !email) {
    return null;
  }

  const trustedMetadata = {
    mcp_connected: groups.includes('mcp_connected') || groups.includes('mcp-connected'),
  };

  return {
    user_id: userId,
    id: userId,
    email,
    emails: email ? [{ email }] : [],
    username,
    preferred_username: preferredUsername,
    groups,
    trusted_metadata: trustedMetadata,
    auth_source: 'oauth2-proxy',
    id_token: idToken,
  };
}

function createAnonymousUser() {
  if (!isAnonymousDashboardEnabled) {
    return null;
  }

  return {
    user_id: anonymousDashboardUserId,
    id: anonymousDashboardUserId,
    email: '',
    emails: [],
    username: anonymousDashboardUserId,
    preferred_username: anonymousDashboardUserId,
    groups: [],
    trusted_metadata: {},
    auth_source: 'anonymous',
    id_token: null,
  };
}

async function fetchProxyUser() {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), PROXY_AUTH_IDENTITY_TIMEOUT_MS) : null;

  let response;
  try {
    response = await fetch('/auth/me', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      ...(controller ? { signal: controller.signal } : {}),
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }

  if (response.status === 401 || response.status === 403 || (isAnonymousDashboardEnabled && response.status === 404)) {
    return createAnonymousUser();
  }

  if (!response.ok) {
    throw new Error(`Failed to load proxy auth identity (${response.status})`);
  }

  const proxyUser = normalizeProxyUser(response.headers);
  if (!proxyUser) {
    return createAnonymousUser();
  }

  return proxyUser;
}

async function loadGlobalAuthorization(user) {
  if (!user || user.auth_source === 'anonymous') {
    return user;
  }
  // These login-origin routes use SDK identity directly. Dashboard membership
  // requests can run with the old cached session while a callback replaces it,
  // then revoke the new session when that obsolete request fails.
  if (user.auth_source === 'stytch' && ['/login', '/authorize', '/logout'].includes(window.location.pathname)) {
    return user;
  }

  try {
    // Login/authorize/logout do not need any dashboard RPC code. Load it only
    // after reaching a dashboard route, and retain memberships for its selector.
    const {getProjectAuthorization} = await import('../utils/projectBootstrap');
    const projectsResponse = await getProjectAuthorization({
      userId: user.id || user.user_id,
      skipAdminMemberships: isSaaSMode && user.auth_source === 'oauth2-proxy',
    });
    const isAdmin = !!projectsResponse?.isHermetiqAdmin;
    console.log('[AppAuth] loadGlobalAuthorization result', {
      userId: user?.id || user?.user_id,
      authSource: user?.auth_source,
      isHermetiqAdmin: isAdmin,
      membershipCount: projectsResponse?.membershipList?.length ?? 0,
      // Skip memberships dump for hermetiq admins — they typically have access
      // to every project and the list would flood the console.
      ...(isAdmin ? {} : {
        memberships: (projectsResponse?.membershipList || []).map(m => ({
          projectId: m.projectId,
          projectName: m.projectName,
          role: m.role,
          settingsEnabled: m.settingsEnabled,
        })),
        rawResponse: projectsResponse,
      }),
    });
    return {
      ...user,
      isHermetiqAdmin: isAdmin,
    };
  } catch (error) {
    if (isUnauthenticatedError(error)) {
      console.warn('[AppAuth] loadGlobalAuthorization auth failure — redirecting to logout', error);
      await handleAuthenticationFailure('loadGlobalAuthorization:unauthenticated', error);
      return user;
    }
    console.warn('Unable to load global user authorization:', error);
    return {
      ...user,
      isHermetiqAdmin: false,
    };
  }
}

function buildAuthValue({ user, isInitialized, refreshUser }) {
  const groups = Array.isArray(user?.groups) ? user.groups : [];
  const trustedMetadata = user?.trusted_metadata || {};

  return {
    user,
    isAuthenticated: !!user,
    isInitialized,
    isHermetiqAdmin: user?.isHermetiqAdmin === true || normalizeMetadataBoolean(trustedMetadata['hermetiq-admin']),
    hasMcpConnected: normalizeMetadataBoolean(trustedMetadata.mcp_connected),
    authSource: user?.auth_source || null,
    groups,
    refreshUser,
  };
}

function StytchAppAuthProvider({ children }) {
  const { user, isInitialized } = useStytchUser();
  const normalizedUser = useMemo(() => normalizeStytchUser(user), [user]);
  const [authorizedUser, setAuthorizedUser] = useState(null);
  const [isAuthorizationLoaded, setIsAuthorizationLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadAuthorization = async () => {
      if (!isInitialized) return;

      if (!normalizedUser) {
        setAuthorizedUser(null);
        setIsAuthorizationLoaded(true);
        return;
      }

      setIsAuthorizationLoaded(false);
      const nextUser = await loadGlobalAuthorization(normalizedUser);
      if (!cancelled) {
        setAuthorizedUser(nextUser);
        setIsAuthorizationLoaded(true);
      }
    };

    loadAuthorization();

    return () => {
      cancelled = true;
    };
  }, [isInitialized, normalizedUser]);

  const refreshUser = useCallback(async () => {
    const nextUser = await loadGlobalAuthorization(normalizedUser);
    setAuthorizedUser(nextUser);
    setIsAuthorizationLoaded(true);
    return nextUser;
  }, [normalizedUser]);

  const value = useMemo(
    () => buildAuthValue({
      user: authorizedUser,
      isInitialized: isInitialized && isAuthorizationLoaded,
      refreshUser,
    }),
    [authorizedUser, isAuthorizationLoaded, isInitialized, refreshUser]
  );

  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}

function ProxyAppAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const refreshUser = async () => {
    const nextUser = await fetchProxyUser();
    setProxyIdToken(nextUser?.id_token || null);
    const authorizedUser = await loadGlobalAuthorization(nextUser);
    setUser(authorizedUser);
    return authorizedUser;
  };

  useEffect(() => {
    let cancelled = false;

    const loadUser = async () => {
      try {
        const nextUser = await fetchProxyUser();
        setProxyIdToken(nextUser?.id_token || null);
        const authorizedUser = await loadGlobalAuthorization(nextUser);
        if (!cancelled) {
          setUser(authorizedUser);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Unable to load proxy auth identity:', error);
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setIsInitialized(true);
        }
      }
    };

    loadUser();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => buildAuthValue({ user, isInitialized, refreshUser }),
    [isInitialized, user]
  );

  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}

export function AppAuthProvider({ children }) {
  if (isStytchEnabled) {
    return <StytchAppAuthProvider>{children}</StytchAppAuthProvider>;
  }

  return <ProxyAppAuthProvider>{children}</ProxyAppAuthProvider>;
}

export function useAppAuth() {
  return useContext(AppAuthContext);
}

export function useAuthUser() {
  const { user, isInitialized } = useAppAuth();
  return { user, isInitialized };
}
